import React, { FC, useEffect, useMemo, useState } from 'react';
import { prop } from 'ramda';

import { Button } from '@interlayer/js/app/components/common/Button';
import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import I18n from '@interlayer/js/components/common/I18n';

import { useTranslations } from '@core/js/hooks/i18n';
import { storageService } from '@core/js/services/storage.service';
import { useUserSelector } from '@core/js/store/hooks';

import { useNavigate } from '@interlayer/js/app/hooks/navigate/useNavigate/useNavigate';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';

import style from './BalancePrompt.scss';

export const BalancePrompt: FC<{ isCasinoEnv?: boolean }> = ({ isCasinoEnv }) => {
    const navigate = useNavigate();
    const firstName = useUserSelector((state) => state.userDetails.firstName || '');
    const playerTags = useUserSelector(prop('allPlayerTags'));
    const t = useTranslations('playtech.mobile.system.header.BalancePrompt.');
    const isShownLowBalancePrompt = storageService.local.getItem('isShownLowBalancePrompt');
    const [
        dTagDepositPageUrl = '/deposit',
        dTagLowBalancePlayerTag = '',
        dTagLowBalanceExcludePlayerTag = ''
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.depositPageUrl,
        DYNAMIC_TAG_NAMES.lowBalancePlayerTag,
        DYNAMIC_TAG_NAMES.lowBalanceExcludePlayerTag
    ]);
    const [isShow, setIsShow] = useState(false);
    const replacers = useMemo(() => {
        if (playerTags?.[dTagLowBalancePlayerTag]?.value) {
            return [firstName, playerTags?.[dTagLowBalancePlayerTag]?.value];
        }

        return [firstName];
    }, [dTagLowBalancePlayerTag, firstName, playerTags]);

    const handleClose = () => {
        storageService.local.setItem('isShownLowBalancePrompt', true);
        setIsShow(false);
    };

    const handleNavigate = () => {
        navigate(dTagDepositPageUrl);

        handleClose();
    };

    useEffect(() => {
        setIsShow(Boolean(
            playerTags?.[dTagLowBalancePlayerTag]
            && !playerTags?.[dTagLowBalanceExcludePlayerTag]
            && !isShownLowBalancePrompt
        ));
    }, [dTagLowBalanceExcludePlayerTag, dTagLowBalancePlayerTag, isShownLowBalancePrompt, playerTags]);

    if (!isShow) {
        return null;
    }

    return (
        <div className={style.wrapper}>
            <SvgIcon
                type="close-popup"
                className={style.close}
                onClick={handleClose}
            />

            <I18n
                t={t}
                tKey="title"
                replacers={replacers}
                className={style.title}
            />

            <I18n
                t={t}
                tKey="text"
                replacers={replacers}
                className={style.text}
            />

            <Button
                size="small"
                view={isCasinoEnv ? 'primary' : 'secondary'}
                className={style.button}
                onClick={handleNavigate}
            >
                {t('button.deposit')}
            </Button>
        </div>
    );
};
